import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { generateFinancialInsight } from '../../../services/openaiService';

const InsightChat = ({ financialData }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query?.trim() || isLoading) return;

    const userMessage = { role: 'user', content: query };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setIsLoading(true);

    try {
      const response = await generateFinancialInsight(query, financialData);
      const assistantMessage = { role: 'assistant', content: response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage = { 
        role: 'assistant', 
        content: error?.message || 'Failed to generate insight. Please try again.',
        isError: true 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-accent/10 rounded-md flex items-center justify-center">
          <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
        </div>
        <h2 className="font-heading font-semibold text-xl text-foreground">
          Ask AI Assistant
        </h2>
      </div>
      <div className="mb-4">
        <div className="bg-muted/30 rounded-lg p-3 max-h-80 overflow-y-auto space-y-3">
          {messages?.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="MessageCircle" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="caption text-muted-foreground">
                Ask questions about your financial data
              </p>
            </div>
          ) : (
            messages?.map((message, index) => (
              <div
                key={index}
                className={`
                  p-3 rounded-lg
                  ${message?.role === 'user' ?'bg-primary/10 ml-8' 
                    : message?.isError
                    ? 'bg-error/10 mr-8' :'bg-card mr-8'
                  }
                `}
              >
                <div className="flex items-start gap-2">
                  <Icon 
                    name={message?.role === 'user' ? 'User' : message?.isError ? 'AlertCircle' : 'Bot'} 
                    size={16} 
                    color={message?.isError ? 'var(--color-error)' : 'var(--color-foreground)'}
                    className="mt-0.5 flex-shrink-0"
                  />
                  <p className={`
                    font-body text-sm
                    ${message?.isError ? 'text-error' : 'text-foreground'}
                  `}>
                    {message?.content}
                  </p>
                </div>
              </div>
            ))
          )}
          {isLoading && (
            <div className="p-3 rounded-lg bg-card mr-8">
              <div className="flex items-center gap-2">
                <Icon name="Bot" size={16} color="var(--color-foreground)" />
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e?.target?.value)}
          placeholder="Ask about trends, expenses, revenue..."
          disabled={isLoading}
          className="flex-1"
        />
        <Button
          type="submit"
          iconName="Send"
          size="icon"
          disabled={!query?.trim() || isLoading}
          loading={isLoading}
        />
      </form>
      <div className="mt-3 flex flex-wrap gap-2">
        {[
          'What are my top expenses?',
          'How is my cash flow trending?',
          'Any unusual transactions?',
        ]?.map((suggestion, index) => (
          <button
            key={index}
            onClick={() => setQuery(suggestion)}
            disabled={isLoading}
            className="px-3 py-1.5 bg-muted/50 hover:bg-muted rounded-md caption text-muted-foreground hover:text-foreground transition-smooth disabled:opacity-50"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
};

export default InsightChat;
